import React, { useState, useEffect, useCallback } from "react";

import api from "../../../../services/api";
import { notify } from "../../../../services/toast.config";

interface GenerateShareLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedForm: any;
  onSuccess: () => Promise<void>;
}

const GenerateShareLinkModal: React.FC<GenerateShareLinkModalProps> = ({
  isOpen,
  onClose,
  selectedForm,
  onSuccess,
}) => {
  const signature = localStorage.getItem("signature");

  // State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customTitle, setCustomTitle] = useState("");

  // Initialize when modal opens
  useEffect(() => {
    if (selectedForm && isOpen) {
      setCustomTitle(""); // Reset title when opening modal
    }
  }, [selectedForm, isOpen]);

  // Form Handlers
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomTitle(e.target.value);
  };

  // Modal Actions
  const handleModalClose = () => {
    setCustomTitle("");
    onClose();
  };

  // Submit Handler
  const handleGenerateLink = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);

      const dataToSend = {
        signature,
        company_id: selectedForm?.CompanyId,
        company_type: selectedForm?.CompanyType,
        form_id: selectedForm?.key,
        title: customTitle.trim() || null,
      };

      const response = await api.post(
        "leadService/forms/share-links/create",
        dataToSend
      );

      const shareableUrl = response.data?.data?.shareable_url || "";

      // Copy to clipboard
      if (shareableUrl) {
        await navigator.clipboard.writeText(shareableUrl);
        notify.success(
          `Share link generated and copied to clipboard successfully!`
        );
      } else {
        notify.success(
          response.data?.message || "Share link generated successfully!"
        );
      }

      handleModalClose();
      await onSuccess(); // Refresh the shared forms list
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.data?.message ||
        "An error occurred while generating the share link.";
      notify.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal fade show"
      style={{
        display: "block",
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0,0,0,0.5)",
        zIndex: 1050,
        overflowY: "auto",
      }}
      tabIndex={-1}
      role="dialog">
      <div className="modal-dialog modal-dialog-centered modal-md">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">Generate Share Link</h4>
            <button
              type="button"
              className="btn-close custom-btn-close"
              onClick={handleModalClose}
              disabled={isSubmitting}
              aria-label="Close">
              <i className="ti ti-x" />
            </button>
          </div>
          <form onSubmit={handleGenerateLink}>
            <div className="modal-body pb-0">
              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="form-label">
                    Form Name <span className="text-muted">(Default)</span>
                  </label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={selectedForm?.LeadFormName || ""}
                    disabled
                  />
                  <small className="text-muted">
                    This is the default form name that will be displayed to
                    users
                  </small>
                </div>

                <div className="col-md-12 mb-3">
                  <label className="form-label">
                    Custom Title{" "}
                    <span className="text-muted">
                      (Optional - For Public UI)
                    </span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter custom title for public display"
                    value={customTitle}
                    onChange={handleTitleChange}
                    disabled={isSubmitting}
                    maxLength={100}
                  />
                  <small className="text-info d-block mt-1">
                    <i className="ti ti-info-circle me-1"></i>
                    If not provided, the default form name will be used on the
                    public website
                  </small>
                </div>

                <div className="col-md-12 mb-3">
                  <div className="alert alert-light border d-flex align-items-start">
                    <i className="ti ti-bulb text-warning me-2 mt-1"></i>
                    <div>
                      <strong>Preview:</strong>
                      <p className="mb-0 mt-1">
                        Public users will see:{" "}
                        <strong className="text-primary">
                          {customTitle.trim() || selectedForm?.LeadFormName}
                        </strong>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="col-md-12 mb-3">
                  <div className="card bg-light border-0">
                    <div className="card-body py-2">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <small className="text-muted d-block">Company</small>
                          <span className="fw-medium">
                            {selectedForm?.CompanyName}
                          </span>
                        </div>
                        <div className="text-end">
                          <small className="text-muted d-block">Type</small>
                          <span className="badge badge-soft-info">
                            {selectedForm?.CompanyType}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-light me-2"
                onClick={handleModalClose}
                disabled={isSubmitting}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    Generating...
                  </>
                ) : (
                  <>
                    <i className="ti ti-share-3 me-2"></i>
                    Generate & Copy Link
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default GenerateShareLinkModal;
